import { useMemo, useState } from 'react';
import { showToast } from './Toast';
import type { Franchisee } from '../types';

type Props = {
  value: string[];
  onChange: (next: string[]) => void;
  franchisees: Franchisee[] | undefined;
  disabled?: boolean;
  placeholder?: string;
};

const DATALIST_ID = 'af-access-franchisees';

export default function AfAccessEditor({
  value,
  onChange,
  franchisees,
  disabled,
  placeholder,
}: Props) {
  const [input, setInput] = useState('');

  const labelByAf = useMemo(() => {
    const m = new Map<string, string>();
    for (const f of franchisees ?? []) m.set(f.af_number, f.marketing_name ?? '');
    return m;
  }, [franchisees]);

  const remaining = useMemo(() => {
    const owned = new Set(value);
    return (franchisees ?? [])
      .filter((f) => !owned.has(f.af_number))
      .sort((a, b) => a.af_number.localeCompare(b.af_number, undefined, { numeric: true }));
  }, [franchisees, value]);

  function tryAdd(raw: string) {
    const trimmed = raw.trim();
    if (!trimmed) return;
    const match = trimmed.match(/^(AF-[0-9.]+)/i);
    const af = match ? match[1].toUpperCase() : trimmed.toUpperCase();
    if (!labelByAf.has(af)) {
      showToast(`Unknown franchisee: ${af}`, { variant: 'error' });
      return;
    }
    if (value.includes(af)) {
      setInput('');
      return;
    }
    onChange([...value, af]);
    setInput('');
  }

  function remove(af: string) {
    onChange(value.filter((x) => x !== af));
  }

  return (
    <div className="af-access-editor">
      {value.length > 0 ? (
        <div className="af-access-pills">
          {value.map((af) => (
            <span key={af} className="af-pill af-pill-removable" title={labelByAf.get(af) || af}>
              {af}
              {!disabled && (
                <button
                  type="button"
                  className="af-pill-remove"
                  onClick={() => remove(af)}
                  aria-label={`Remove ${af}`}
                >
                  ×
                </button>
              )}
            </span>
          ))}
        </div>
      ) : (
        <div className="af-access-empty">No franchise access yet.</div>
      )}
      {!disabled && (
        <div className="af-access-input-row">
          <input
            type="text"
            list={DATALIST_ID}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={placeholder ?? 'Add by AF number or name…'}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                tryAdd(input);
              }
            }}
          />
          <datalist id={DATALIST_ID}>
            {remaining.map((f) => (
              <option key={f.af_number} value={f.af_number}>
                {f.marketing_name ?? ''}
              </option>
            ))}
          </datalist>
          <button
            type="button"
            className="ds-btn ds-btn-secondary"
            onClick={() => tryAdd(input)}
            disabled={!input.trim()}
          >
            Add
          </button>
        </div>
      )}
    </div>
  );
}
